import { AddressFile } from '@provision/entity-types';
import { useMutation } from '@provision/graphql-client';
import { useCallback } from 'react';
import { DELETE_ADDRESS_FILE_MUTATION } from 'src/graphql/entities/address-file/address-file.operations';
import { filePath } from 'src/graphql/entities/address-file/utils';
import { useDeleteFile } from 'src/graphql/entities/files-upload';
import { ConditionInput } from 'src/graphql/types';
import { useNotifications } from 'src/utils/hooks';
import { GetAddressFilesQuery } from './use-address-files';

export interface DeleteAddressFileMutation {
  deleteAddressFile: (Pick<AddressFile, 'id'> & { __typename: string })[];
}

export type DeleteAddressFileMutationParams = {
  conditions: ConditionInput[];
};

export const useDeleteAddressFile = () => {
  const { showErrorAlert } = useNotifications();
  const { deleteFile } = useDeleteFile();
  const [deleteAddressFileMutation, { loading: isDeleting }] = useMutation<
    DeleteAddressFileMutation,
    DeleteAddressFileMutationParams
  >(DELETE_ADDRESS_FILE_MUTATION, {
    update(cache, { data }) {
      if (!data?.deleteAddressFile?.length) {
        return;
      }

      cache.modify({
        fields: {
          getAddressFile(cachedRef = {}) {
            const cachedData = cachedRef as GetAddressFilesQuery['getAddressFile'];
            return {
              ...cachedData,
              records: cachedData.records?.filter(
                (file) => file.id !== data.deleteAddressFile[0].id,
              ),
              metadata: {
                ...cachedData.metadata,
                totalRecords: (cachedData.metadata?.totalRecords ?? 0) - 1,
              },
            };
          },
        },
      });
      data.deleteAddressFile.forEach((file) => {
        const id = cache.identify({
          __typename: 'AddressFile',
          id: file.id,
        });
        cache.evict({ id });
      });
      cache.gc();
    },
    onError: (error) => {
      console.error(error);
      showErrorAlert('Failed to delete file, please try again');
    },
  });

  const deleteAddressFile = useCallback(
    async (file: AddressFile) => {
      if (!file.id) {
        return;
      }

      void deleteFile(filePath(file.partnerId, file.addressId, file.fileKey));
      await deleteAddressFileMutation({
        variables: {
          conditions: [
            {
              attribute: 'addressFileId',
              operator: '=',
              value: file.id,
            },
          ],
        },
        optimisticResponse: {
          deleteAddressFile: [
            {
              __typename: 'AddressFile',
              id: file.id,
            },
          ],
        },
      });
    },
    [deleteAddressFileMutation, deleteFile],
  );

  return {
    deleteAddressFile,
    isDeleting,
  };
};
